import * as React from "react"
import EditCommodityRatesPage from "@/routes/_home/meter-connection-settings/commodity-rates/edit"
import EditConnectionStatusPage from "@/routes/_home/meter-connection-settings/connection-status/edit"
import EditMeterSizeRatesPage from "@/routes/_home/meter-connection-settings/meter-size-rates/edit"
import { ConnectionStatusType } from "@/types"
import { zodResolver } from "@hookform/resolvers/zod"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useForm } from "react-hook-form"
import * as z from "zod"

import { CommodityRate, MeterSizeRateType } from "@/types/meter"
import { validateUserDeletePermission } from "@/lib/api/auth"
import { deleteCommodityRate } from "@/lib/api/commodity-rates"
import { deleteConnectionStatus } from "@/lib/api/connection-status"
import { deleteMeterSizeRate } from "@/lib/api/meter"
import { useToast } from "@/hooks/use-toast"
import { Button } from "@/components/ui/button"
import {
  Credenza,
  CredenzaBody,
  CredenzaClose,
  CredenzaContent,
  CredenzaDescription,
  CredenzaFooter,
  CredenzaHeader,
  CredenzaTitle,
} from "@/components/ui/credenza"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Icons } from "@/components/icons"

interface DataProps {
  data: MeterSizeRateType
}

export function MeterSizeRateOperation({ data }: DataProps) {
  const [showDropDown, setShowDropDown] = React.useState<boolean>(false)
  const [showDeleteModal, setShowDeleteModal] = React.useState<boolean>(false)
  const [showEditModal, setShowEditModal] = React.useState<boolean>(false)
  return (
    <div>
      <DropdownMenu open={showDropDown} onOpenChange={setShowDropDown}>
        <DropdownMenuTrigger>
          <div className="flex h-8 w-8 items-center justify-center rounded-md border transition-colors hover:bg-muted">
            <Icons.ellipsis className="size-4" />
            <span className="sr-only">Open</span>
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            onSelect={() => {
              setShowEditModal(true)
              setShowDropDown(false)
            }}
          >
            <Icons.edit className="mr-2 size-4" />
            Edit Meter Size Rate
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem>
            <Icons.copy className="mr-2 size-4" />
            Copy Meter Size Rate
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem>
            <Icons.paste className="mr-2 size-4" />
            Paste Meter Size Rate
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="text-destructive"
            onSelect={() => {
              setShowDeleteModal(true)
              setShowDropDown(false)
            }}
          >
            <Icons.archive className="mr-2 size-4" />
            Archive
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Edit Modal */}
      <Credenza open={showEditModal} onOpenChange={setShowEditModal}>
        <EditMeterSizeRatesPage
          meterSizeRatesData={data}
          setShowEditMeterSizeRatesModal={setShowEditModal}
        />
      </Credenza>

      {/* Delete */}
      <DeleteMeterSizeRate
        meterSizeRateId={data.id ?? 0}
        meterSizeRate={String(data.id) ?? ""}
        showModal={showDeleteModal}
        setShowModal={setShowDeleteModal}
      />
    </div>
  )
}

function DeleteMeterSizeRate({
  meterSizeRateId,
  meterSizeRate,
  showModal,
  setShowModal,
}: {
  meterSizeRateId: number
  meterSizeRate: string
  showModal: boolean
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>
}) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const formSchema = z.object({
    id: z.coerce.number(),
    selected_meter_size_rate: z
      .string()
      .min(1, "Required")
      .refine((val) => val === meterSizeRate, {
        message: "Selected Meter Size Rate must match the Meter Size Rate.",
      }),
  })

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      id: meterSizeRateId,
      selected_meter_size_rate: "",
    },
  })

  const mutation = useMutation({
    mutationFn: async () => {
      await deleteMeterSizeRate(meterSizeRateId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["metersizerates"] })
      toast({
        description: "Commodity Rate deleted successfully.",
      })

      form.reset()
      setShowModal(false)
    },
    onError: (error: unknown) => {
      toast({
        title: "Something went wrong!",
        description: `${error}`,
        variant: "destructive",
      })
    },
  })

  async function onSubmit() {
    if (!validateUserDeletePermission()) return
    mutation.mutate()
  }

  return (
    <>
      <Credenza open={showModal} onOpenChange={setShowModal}>
        <CredenzaContent>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="space-y-4 pb-4"
            >
              <CredenzaHeader>
                <CredenzaTitle>Delete Commodity Rate</CredenzaTitle>
                <CredenzaDescription>
                  You cannot undo this action!
                </CredenzaDescription>
              </CredenzaHeader>
              <CredenzaBody>
                <FormField
                  control={form.control}
                  name="selected_meter_size_rate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Type "{meterSizeRate}" to delete.</FormLabel>
                      <FormControl>
                        <Input {...field} autoComplete="off" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CredenzaBody>
              <CredenzaFooter>
                <CredenzaClose asChild>
                  <Button variant={"outline"}>Cancel</Button>
                </CredenzaClose>
                <Button
                  disabled={mutation.isPending}
                  type="submit"
                  variant={"destructive"}
                >
                  {mutation.isPending && (
                    <Icons.loader className="mr-2 animate-spin" />
                  )}
                  Delete
                </Button>
              </CredenzaFooter>
            </form>
          </Form>
        </CredenzaContent>
      </Credenza>
    </>
  )
}
